import { Center, Loader } from "@mantine/core";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router";
import { useMe } from "../api/auth";
import type { Permission } from "../api/types";
import { changeLanguage, languages, type Language } from "../i18n/i18n";
import { NotFoundPage, RouteErrorPage } from "./ErrorPages";

/** Chráněná část aplikace: nepřihlášeného pošle na přihlášení a vrátí ho zpět na původní adresu. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const { i18n } = useTranslation();
  const userLanguage = me.data?.language;

  // Jazyk rozhraní podle uživatele (F-ADM-05).
  useEffect(() => {
    if (userLanguage && userLanguage !== i18n.resolvedLanguage && languages.includes(userLanguage as Language))
      void changeLanguage(userLanguage as Language);
  }, [userLanguage, i18n.resolvedLanguage]);

  if (me.isPending)
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  if (me.isError) return <RouteErrorPage />;
  if (!me.data) {
    const next = location.pathname + location.search;
    return <Navigate to={next === "/" ? "/prihlaseni" : `/prihlaseni?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
}

/** Obsah jen pro uživatele s oprávněním; ostatním se tváří jako neexistující stránka (oprávnění hlídá i server). */
export function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const me = useMe();
  return me.data?.permissions.includes(permission) ? children : <NotFoundPage />;
}
